import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Plus, Minus, Phone, ArrowRight } from "lucide-react";

const FAQS = [
  {
    q: "How far in advance should I book?",
    a: "We recommend booking 2-4 weeks ahead, especially for weekends and end-of-month dates. That said, we do take last-minute bookings when we can — just give us a call."
  },
  {
    q: "Do you provide packing materials and packing service?",
    a: "Yes! We offer a full professional packing service and supply all materials — boxes, bubble wrap, tape, wardrobe boxes, the lot. You can add this when getting your quote, or just ask us on the day."
  },
  {
    q: "Are my belongings insured during the move?",
    a: "Absolutely. All moves are covered by our comprehensive goods-in-transit insurance up to £50,000 as standard. We also carry full public liability insurance for your property."
  },
  {
    q: "What's the difference between an instant estimate and a survey quote?",
    a: "An instant estimate gives you a quick price based on your move details — perfect for smaller, straightforward moves. A survey quote means we visit your property to assess everything in person, giving you an exact price. Surveys are free and there's no obligation."
  },
  {
    q: "Do I need to pay a deposit?",
    a: "Yes, we ask for a 25% deposit to secure your date. The balance is due on moving day. You can pay the deposit online by card when you book."
  },
  {
    q: "Can you move specialist items like pianos or antiques?",
    a: "Yes — we have specialist equipment and trained staff for pianos, antiques, artwork, safes, and other delicate items. These moves require a survey so we can plan properly and give you an accurate price."
  },
  {
    q: "What areas do you cover?",
    a: "We're based in Cardiff with a dedicated team in London. We cover all of Wales and England, including long-distance moves between the two. Give us a call for moves to Scotland or further afield."
  },
  {
    q: "What happens if you damage something?",
    a: "It's rare, but it can happen. If it does, our insurance covers it. We'll handle the claim process for you and get it sorted as quickly as possible. We also use floor protectors, furniture blankets, and banister wraps on every single move."
  },
];

export const FAQAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="section-padding bg-white" data-testid="faq-accordion-section">
      <div className="container-max">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-2 block">Got Questions?</span>
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold tracking-tight text-[#0F172A] mt-2">
              We've Got Answers
            </h2>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={faq.q}
                  className={`border transition-colors ${isOpen ? "border-[#FF5A1F]/30 bg-[#FF5A1F]/[0.02]" : "border-slate-200"}`}
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left group"
                    data-testid={`faq-toggle-${index}`}
                  >
                    <span className="font-['Outfit'] font-semibold text-[#0F172A] pr-4 leading-snug">
                      {faq.q}
                    </span>
                    <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center transition-colors ${isOpen ? "bg-[#FF5A1F] text-white" : "bg-slate-100 text-slate-500 group-hover:bg-[#FF5A1F]/10"}`}>
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </div>
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Still have questions? */}
          <div className="mt-10 pt-8 border-t border-slate-200 text-center" data-testid="faq-cta">
            <p className="text-slate-600 text-sm mb-4">Still have questions? We're happy to help.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5A1F] text-white font-semibold px-6 py-3 text-sm hover:bg-[#E54E18] transition-colors"
              >
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:07346209593"
                className="inline-flex items-center justify-center gap-2 border border-slate-300 text-[#0F172A] font-semibold px-6 py-3 text-sm hover:bg-slate-50 transition-colors"
              >
                <Phone className="w-4 h-4" /> 07346 209593
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
